// ATTP NEXUS — Public-facing site content (Engineering, Corporate Hub, About Us)
// IMPORTANT: do NOT summarize. Content is presented in full per business spec.

/* ====================================================================
 *  ENGINEERING — Olu-Akintade Computer & Electrical Works Engineering
 * ==================================================================== */

export const ENGINEERING_BRAND = {
  legalName: 'Olu-Akintade Computer & Electrical Works Engineering',
  shortName: 'Olu-Akintade Engineering',
  division: 'ATTP Engineering Division',
  tagline: 'Electrical · Computers · Electronics · Networks · Industrial Systems',
  logo: '/logos/oluakin-engineering.png',
  primary: '#0B1320',       // deep black-navy
  accent: '#E63946',        // brand red
  secondary: '#F4A261',     // copper / amber accent
  email: 'info@oluakin.com.ng',
  phone: '+234 09154498281',
  address: 'Lagos · Nigeria',
};

export const ENGINEERING_SERVICES = [
  {
    slug: 'computer-repairs',
    title: 'Computer Repairs & Maintenance',
    short: 'Comprehensive computer diagnosis, repair and lifecycle support',
    hero: 'https://images.unsplash.com/photo-1517490232338-06b912a786b5?w=1400&q=80',
    intro:
      'Comprehensive diagnosis, repair, maintenance, upgrade and optimization of computers and IT equipment for individuals, schools, businesses, and institutions. Our certified technicians work on every major brand of laptop, desktop and server using genuine parts and rigorous testing.',
    body:
      'From single-laptop emergencies to full-scale IT environments running across multiple campuses, our computer repairs and maintenance practice delivers measurable uptime improvements. We combine component-level diagnostics, calibrated tooling and a transparent repair workflow — every device that enters our workshop is logged, tested, repaired, retested and signed off before release.',
    items: [
      'Desktop computer repairs',
      'Laptop repairs',
      'Motherboard diagnosis and repair',
      'Laptop screen replacement',
      'Keyboard replacement',
      'Battery replacement',
      'RAM upgrades',
      'Hard drive replacement',
      'SSD installation and migration',
      'Operating system installation',
      'Virus and malware removal',
      'Data backup and recovery',
      'Software troubleshooting',
      'Network troubleshooting',
      'Printer connectivity support',
      'Server maintenance',
      'Preventive maintenance services',
      'Annual maintenance contracts',
    ],
    closing:
      'Whether you are a school protecting student labs, a business safeguarding workstations, or a household needing one-off attention, our computer repair workflow ensures fast turnaround and durable results.',
  },
  {
    slug: 'electrical-installations',
    title: 'Electrical Installations & Power Solutions',
    short: 'End-to-end electrical engineering for residential, commercial and industrial premises',
    hero: 'https://images.unsplash.com/photo-1565608438257-fac3c27beb36?w=1400&q=80',
    intro:
      'Professional electrical engineering services for residential, commercial, educational, and industrial facilities. Designs are prepared in accordance with the Nigerian Wiring Regulations, IEE BS 7671 and IEC 60364 — installed by certified electricians and signed off with full test certificates.',
    body:
      'Our electrical installation practice spans new-build wiring, large rewiring projects, distribution upgrades, renewable energy systems and ongoing preventive maintenance. We design with safety, efficiency and future expansion in mind so the systems we deliver stand up to years of demanding daily use.',
    items: [
      'Complete building electrical wiring',
      'Electrical rewiring projects',
      'Electrical fault diagnosis',
      'Distribution board installations',
      'Panel upgrades',
      'Circuit breaker installations',
      'Earthing systems',
      'Generator installations',
      'Generator changeover systems',
      'Solar energy installations',
      'Inverter installations',
      'UPS systems',
      'Lighting system design',
      'Street lighting solutions',
      'Industrial electrical installations',
      'Energy efficiency solutions',
      'Preventive electrical maintenance',
    ],
    closing:
      'Every installation we deliver is documented, tested and supported with a clear warranty. Our team is prepared to design and install one office, an entire estate, or a multi-building campus.',
  },
  {
    slug: 'electronics-repairs',
    title: 'Electronics Repairs',
    short: 'Component-level repair of consumer and professional electronics',
    hero: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&q=80',
    intro:
      'Professional repair and restoration of electronic devices and equipment. From flat-screen televisions to public address systems and amplifiers, we operate as a full electronics workshop with hot-air rework stations, signal analysers and an extensive component stock.',
    body:
      'We diagnose at component-level rather than swapping boards — saving cost and extending equipment lifespan. Where parts are unavailable locally we source from trusted regional distributors, with every repair carrying a written warranty.',
    items: [
      'Television repairs',
      'LED TV repairs',
      'Smart TV repairs',
      'Home theatre systems',
      'Public address systems',
      'Audio amplifiers',
      'DVD systems',
      'Consumer electronics troubleshooting',
      'Appliance repairs',
      'Component-level diagnostics',
      'Circuit board repairs',
      'Electronic equipment servicing',
    ],
    closing:
      'Bring it in, ship it to us, or schedule on-site service for fragile or large equipment. Our team treats every job as a precision engineering task.',
  },
  {
    slug: 'cctv-security',
    title: 'CCTV & Security Systems',
    short: 'Modern surveillance and access-control infrastructure',
    hero: 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=1400&q=80',
    intro:
      'Modern security and surveillance solutions for homes, schools, offices, estates, and industrial facilities. We design, deploy and maintain end-to-end security ecosystems combining surveillance cameras, access control, biometric attendance and security lighting.',
    body:
      'Each project begins with a site survey and threat assessment. Our engineers specify equipment from leading manufacturers — Hikvision, Dahua, ZKTeco, Suprema — and configure remote monitoring portals so site managers have visibility from any device, anywhere.',
    items: [
      'CCTV installation',
      'IP camera deployment',
      'DVR systems',
      'NVR systems',
      'Remote monitoring systems',
      'Access control systems',
      'Biometric attendance systems',
      'Time attendance systems',
      'Video door phones',
      'Electric fencing integration',
      'Security lighting systems',
      'Security infrastructure consulting',
      'Surveillance maintenance contracts',
    ],
    closing:
      'Investing in modern security infrastructure is investing in operational continuity. We deliver systems that protect people, assets and reputation.',
  },
  {
    slug: 'networking-it',
    title: 'Networking & IT Infrastructure',
    short: 'Enterprise-grade structured cabling, wireless and connectivity engineering',
    hero: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1400&q=80',
    intro:
      'Design, deployment, and support of enterprise-grade networking and ICT infrastructure. From small business LANs to multi-campus fibre networks, our network engineers deliver reliable, scalable infrastructure built on industry best practice.',
    body:
      'We deliver structured cabling certified to Cat6 / Cat6A and OS2 / OM4 fibre standards, supplemented with rigorous documentation: as-built drawings, port mapping, IP plans and runbooks. The result is a network your IT team can confidently operate, expand and audit.',
    items: [
      'Structured network cabling',
      'Fibre optic deployment',
      'LAN implementation',
      'WAN implementation',
      'Wi-Fi design and deployment',
      'Router configuration',
      'Switch configuration',
      'Server room setup',
      'Internet sharing solutions',
      'School ICT infrastructure',
      'Corporate ICT infrastructure',
      'Cloud integration support',
      'Network security implementation',
      'IT consulting services',
    ],
    closing:
      'Connectivity is the backbone of every modern organisation. We build networks that are fast today and ready for tomorrow.',
  },
  {
    slug: 'industrial-equipment',
    title: 'Industrial Equipment & Heavy Systems',
    short: 'Servicing of industrial, institutional and heavy electrical equipment',
    hero: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1400&q=80',
    intro:
      'Maintenance and servicing of industrial and institutional equipment. We support factories, hospitals, large schools and campuses with periodic servicing, fault response and reliability engineering for their heavy electrical and electronic systems.',
    body:
      'Our industrial team handles generator overhauls, UPS battery cycling, motor servicing, industrial wiring upgrades and the integration of automation controls. We work to documented preventive-maintenance schedules and provide written condition reports with each visit.',
    items: [
      'Generator servicing',
      'UPS maintenance',
      'Industrial wiring',
      'Machine repairs',
      'Industrial control systems',
      'Preventive maintenance schedules',
      'Equipment diagnostics',
      'Electrical motor servicing',
      'Power distribution systems',
      'Industrial automation support',
      'Facility engineering support',
    ],
    closing:
      'When uptime matters, partner with engineers who treat your equipment as an asset to be preserved — not a problem to be patched.',
  },
  {
    slug: 'sales-supply',
    title: 'Sales & Supply Division',
    short: 'Supply of quality engineering and ICT products',
    hero: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1400&q=80',
    intro:
      'Supply of quality products and engineering materials. As an authorised dealer for several manufacturers, we source genuine, warranty-backed equipment for individuals, institutions and corporate buyers.',
    body:
      'Bulk procurement enquiries receive personalised attention with structured quotations, delivery scheduling and post-sales support. We are equally comfortable supplying a single replacement laptop or specifying a full classroom of computers and projectors.',
    items: [
      'Laptops',
      'Desktop computers',
      'Printers',
      'Networking equipment',
      'CCTV equipment',
      'Electrical materials',
      'Solar equipment',
      'Inverters',
      'Batteries',
      'Office equipment',
      'Industrial electrical components',
      'Spare parts',
      'Bulk procurement support',
    ],
    closing:
      'From a single laptop to a 200-unit deployment, our supply division gives institutional buyers consistency, accountability and competitive pricing.',
  },
  {
    slug: 'maintenance-contracts',
    title: 'Maintenance Contracts',
    short: 'Annual and recurring engineering maintenance programmes',
    hero: 'https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?w=1400&q=80',
    intro:
      'A dedicated programme of annual and recurring maintenance support designed for educational institutions, corporate clients and industrial operators. Predictable cost, faster response, longer equipment life.',
    body:
      'Our maintenance contracts shift your operations from break-fix to preventive. We schedule inspections, complete condition assessments and act before failures occur. Each contract is tailored — coverage, response times, included consumables and reporting cadence are all negotiated to suit your environment.',
    items: [
      'Monthly inspections',
      'Quarterly inspections',
      'Preventive maintenance',
      'Emergency response services',
      'Priority support',
      'Technical audits',
      'Maintenance reporting',
      'Equipment lifecycle monitoring',
      'Educational institution support',
      'Corporate maintenance programmes',
    ],
    closing:
      'Contracted clients receive priority deployment, transparent monthly reporting, and structured cost forecasting — predictability is the most overlooked engineering asset.',
  },
];

/* ====================================================================
 *  ATTP CORPORATE HUB — Business Consulting · Training · Education
 * ==================================================================== */

export const CORPORATE_BRAND = {
  legalName: 'ATTP Corporate Hub',
  shortName: 'ATTP Corporate Hub',
  division: 'Consulting · Training · Professional Education',
  tagline: 'Building stronger African institutions through structure, leadership and growth',
  logo: '/logos/attp-corporate-hub.png',
  primary: '#1C1C1C',
  accent: '#C9A84C',
  secondary: '#8E6A1F',
  email: 'OA@attpnexus.com.ng',
  phone: '+234 (803) 517 5136',
  address: 'Active across 13+ African countries',
};

export const CORPORATE_PILLARS = [
  {
    slug: 'business-consulting',
    title: 'Business Consulting',
    short: 'Strategic advisory, organisational design and institutional transformation',
    hero: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1400&q=80',
    intro:
      'A dedicated business-consulting practice helping African institutions design and run structured operations. We work alongside leadership teams to translate strategy into systems, build governance, and put discipline around how the organisation operates day-to-day.',
    body:
      'Our consultants combine cross-sector experience — corporate, educational, public sector — with rigorous methodologies. Each engagement is shaped around the client: a diagnostic phase, a design phase, an implementation phase, and a sustained handover. We do not deliver thick reports and disappear; we walk with the institution until the new systems are operating.',
    items: [
      'Strategic advisory services',
      'Organisational design',
      'Institutional restructuring',
      'Governance frameworks',
      'Operational systems development',
      'Human resource systems',
      'Performance management systems',
      'Procurement systems',
      'Inventory management systems',
      'Facility management systems',
      'Fleet management systems',
      'School administration systems',
      'Multi-campus management structures',
      'Policy development',
      'Corporate governance',
      'Cost control systems',
      'Budgeting systems',
      'Institutional transformation programmes',
    ],
    closing:
      'If your institution is preparing for growth, restructuring, regulatory pressure, or a new strategic chapter — we are ready to help you build the structures that will carry the next decade.',
  },
  {
    slug: 'corporate-training',
    title: 'Corporate Training',
    short: 'Capacity-building programmes for leaders, managers and operational staff',
    hero: 'https://images.unsplash.com/photo-1531538606174-0f90ff5dce83?w=1400&q=80',
    intro:
      'A structured portfolio of corporate training programmes covering leadership, supervisory development, administrative excellence and operational disciplines. Programmes are delivered in-person, virtually or in blended formats — all anchored on real workplace scenarios drawn from African organisations.',
    body:
      'We design training that is practical, measurable and tied to the strategic priorities of the organisation. Every programme includes pre-engagement diagnostics, structured content delivery, applied workshop exercises, and a post-training follow-through window so behaviour change is reinforced on the job.',
    items: [
      'Leadership development',
      'Supervisory development',
      'Administrative excellence',
      'Human resource management',
      'Customer service excellence',
      'Stakeholder engagement',
      'Workplace ethics',
      'Accountability culture',
      'Procurement training',
      'Inventory management',
      'Facility management',
      'School administration training',
      'Reporting standards',
      'Communication skills',
      'Workplace productivity',
      'Team effectiveness',
      'Performance improvement',
    ],
    closing:
      'Programmes are credentialled, customisable and supported by post-training mentoring. We will tailor delivery to single-team workshops, full-year management academies, or organisation-wide capacity-building.',
  },
  {
    slug: 'professional-education',
    title: 'Professional Education',
    short: 'Education-sector consulting and leadership development for schools',
    hero: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1400&q=80',
    intro:
      'A dedicated education practice supporting schools, multi-campus systems and educational regulators. We bring deep, practitioner-level insight into the day-to-day reality of running educational institutions — from admissions desks to boarding houses, from classroom operations to compliance audits.',
    body:
      'Our work spans single-school turnarounds, multi-campus restructurings, leadership academies for principals, and institutional growth planning for expanding school groups. Programmes are delivered by senior practitioners with first-hand experience of running schools and educational systems.',
    items: [
      'School administration programmes',
      'Educational leadership',
      'Academic operations management',
      'Multi-campus management',
      'Boarding school administration',
      'Student welfare systems',
      'Staff development systems',
      'Educational compliance',
      'Regulatory readiness',
      'Admissions management',
      'Parent relations systems',
      'School safety systems',
      'Educational technology integration',
      'Institutional growth planning',
      'School expansion strategy',
    ],
    closing:
      'Whether you operate a single school or a network of campuses, we partner with you to professionalise operations, develop leadership and prepare for sustainable growth.',
  },
];

/* ====================================================================
 *  ABOUT US — page structure & content
 * ==================================================================== */

export const ABOUT_PAGES = [
  { slug: 'about-attp', label: 'About ATTP Corporate Hub' },
  { slug: 'mission-vision', label: 'Mission & Vision' },
  { slug: 'core-values', label: 'Core Values' },
  { slug: 'lead-consultant', label: 'Lead Consultant & Founder' },
  { slug: 'countries-of-operation', label: 'Countries of Operation' },
  { slug: 'why-choose-us', label: 'Why Choose Us' },
  { slug: 'our-approach', label: 'Our Approach' },
  { slug: 'contact-us', label: 'Contact Us' },
];

export const ABOUT_CONTENT = {
  'about-attp': {
    title: 'About ATTP Corporate Hub',
    intro:
      'ATTP Corporate Hub is a professional consulting, training and education practice dedicated to helping African organisations build structured systems, develop capable leaders and achieve sustainable institutional growth.',
    sections: [
      {
        heading: 'Who we are',
        body:
          'We are a multi-disciplinary team of consultants, trainers and educators with operational experience across 13+ African countries. Our work spans the corporate, educational, public-sector and not-for-profit segments — wherever an institution needs structure, capability and accountability.',
      },
      {
        heading: 'What we do',
        body:
          'Through three integrated pillars — Business Consulting, Corporate Training and Professional Education — we help institutions operate more effectively, develop stronger leaders and grow with confidence. Our engagements range from short diagnostic studies to multi-year transformation programmes.',
      },
      {
        heading: 'How we work',
        body:
          'Every engagement begins with deep listening. We diagnose, design, build and walk alongside our clients until the new systems are operating and the new capability is embedded. We deliver practical, applied solutions — not abstract theory — and we measure ourselves on the operational outcomes our clients achieve.',
      },
    ],
  },
  'mission-vision': {
    title: 'Mission & Vision',
    intro:
      'A clear vision and a disciplined mission define why we exist and how we operate.',
    sections: [
      {
        heading: 'Our Vision',
        body:
          'To strengthen African institutions through structured systems, capable leadership and sustainable growth — building organisations that can carry the weight of their ambition for generations to come.',
      },
      {
        heading: 'Our Mission',
        body:
          'To deliver practical, high-impact consulting, training and education solutions tailored to African institutional realities — equipping organisations with the structure, leaders and operating discipline they need to thrive.',
      },
    ],
  },
  'core-values': {
    title: 'Core Values',
    intro: 'The values that govern every engagement we accept and every relationship we hold.',
    values: [
      { name: 'Structure', body: 'Predictable, repeatable, audit-ready operations.' },
      { name: 'Excellence', body: 'Continental ambition, global-grade delivery.' },
      { name: 'Practicality', body: 'Applied solutions that work in real African contexts.' },
      { name: 'Integrity', body: 'Transparent advice, honest reporting, ethical engagement.' },
      { name: 'Impact', body: 'Outcomes that endure beyond the engagement.' },
      { name: 'Partnership', body: 'We walk alongside clients — not above them.' },
    ],
  },
  'lead-consultant': {
    title: 'Lead Consultant & Founder',
    name: 'Olusegun Akintade, MA · PGD · BEng · BSc',
    role: 'Lead Consultant and Founder, ATTP Corporate Hub',
    quote:
      'Sustainable institutional success is achieved through structured processes, strong governance, and disciplined execution.',
    portrait: '/portraits/oa-lead-consultant.jpg',
    biography:
      'Olusegun Akintade is the founder of ATTP Corporate Hub and a seasoned institutional development expert with more than two decades of cross-sector experience. His career spans education leadership, organisational consulting, ICT integration and operational systems design — work that has taken him into more than 13 African countries supporting schools, corporates, NGOs and government agencies as they design and rebuild the structures that carry institutional ambition.',
    careerHighlights: [
      'Twenty-plus years of leadership across education, consulting and digital transformation',
      'Founder and Lead Consultant of ATTP Corporate Hub',
      'Multi-country institutional development engagements across West, East and Southern Africa',
      'Designer of school administration and multi-campus management frameworks adopted by leading institutions',
      'Architect of ATTP NEXUS — the integrated operational platform powering African organisations',
    ],
    philosophy:
      'Olusegun believes that organisational outcomes are downstream of organisational structure. His leadership philosophy is rooted in three principles: design before you deploy, document before you delegate, and review before you redirect. He works with leaders to translate vision into operating systems — institutional choreography that survives the founder.',
    methodology:
      'Every engagement under his leadership follows a four-phase discipline — Diagnose, Design, Deploy, Sustain. The Diagnose phase is rigorous: interviews, document review, system walk-throughs and on-site observation. Design produces working systems, not slides. Deploy hands them over with full training, change management and performance instrumentation. Sustain is the unglamorous but vital final mile — six to twelve months of accompaniment until the new systems are embedded.',
    certifications: [
      'Master of Arts — Educational Leadership',
      'Postgraduate Diploma — Management',
      'Bachelor of Engineering',
      'Bachelor of Science — Educational Studies',
      'Certified School Administrator (multiple national bodies)',
      'Certified Trainer — Leadership and Organisational Development',
    ],
    specialisations: [
      'Organisational design and institutional restructuring',
      'Education systems development and school administration',
      'Multi-campus management and boarding school operations',
      'Corporate training and leadership development',
      'Human resource systems and performance management',
      'Procurement, inventory and facility management systems',
      'Digital transformation and ERP / ICT integration',
      'Strategic advisory and policy development',
    ],
    industries: [
      'Education (primary, secondary, tertiary, multi-campus systems)',
      'Healthcare (hospitals, clinics, diagnostic centres)',
      'Corporate and conglomerate operations',
      'Public sector and government agencies',
      'Estates, facility management and mixed-use developments',
      'Faith-based and non-governmental organisations',
    ],
    panAfrican:
      'Olusegun has delivered engagements across more than 13 African countries — supporting institutions in West Africa, East Africa and Southern Africa with restructurings, leadership academies, governance reviews and digital-transformation programmes. His Pan-African experience has produced a deep, ground-truth understanding of the operating realities African institutions actually face — talent constraints, infrastructure variability, regulatory complexity, multi-jurisdictional growth.',
    accomplishments: [
      'Designed and deployed the school administration framework now used by leading multi-campus school groups in West Africa',
      'Led institutional restructurings that returned struggling institutions to operational stability and growth',
      'Built leadership academies that have trained hundreds of school principals, heads of department and senior administrators',
      'Authored governance and policy frameworks adopted by educational regulators and individual institutions',
      'Founded ATTP NEXUS — the digital operating platform built specifically to give African institutions an integrated, structured way to run',
    ],
    trainingExperience:
      'Across his career Olusegun has designed and delivered training programmes for principals, boards of governors, school management teams, HR teams, administrative officers, procurement teams and senior executives. He facilitates with the practitioner\'s authority — every session is grounded in lived institutional experience and rich with worked examples drawn from real African case work.',
    educationalLeadership:
      'A practitioner of educational leadership, Olusegun has led, advised and developed schools and school systems for over two decades. His leadership work covers academic operations, boarding administration, staff development, student welfare, parent relations, regulatory readiness and growth planning. He understands the texture of running a school — and that understanding shapes every consulting engagement.',
    orgDevExpertise:
      'Olusegun specialises in the practical, working-level mechanics of organisational development — designing functional structures, building HR systems, putting performance management on a rigorous footing, creating procurement and inventory discipline, and aligning policy frameworks with the realities of African operating environments. He works with leadership teams who are willing to invest in the structural foundation that supports sustained growth.',
    digitalTransformation:
      'A long-standing advocate of practical digital transformation, Olusegun champions African-built systems that fit African operating realities. His work has helped institutions implement ERP solutions, automate manual workflows, digitise reporting, and modernise administrative operations. ATTP NEXUS itself is the institutional embodiment of that conviction.',
    strategicAdvisory:
      'Senior leaders engage Olusegun for confidential strategic advisory — restructurings, succession planning, board-level governance reviews, growth strategy and turnaround engagements. Conversations are direct, evidence-based and oriented around the structural decisions that will determine institutional trajectory.',
    contact: {
      email: 'oa@attpnexus.com.ng',
      phone: '+234 (803) 517 5136',
      address: 'Nigeria · Active across 13+ African countries',
    },
  },
  'countries-of-operation': {
    title: 'Countries of Operation',
    intro:
      'ATTP Corporate Hub is active across more than 13 African countries — delivering consulting, training and education-sector engagements with consistent global-standard quality.',
    countries: [
      { name: 'Nigeria', region: 'West Africa', flag: '🇳🇬' },
      { name: 'Ghana', region: 'West Africa', flag: '🇬🇭' },
      { name: 'Senegal', region: 'West Africa', flag: '🇸🇳' },
      { name: 'Côte d\'Ivoire', region: 'West Africa', flag: '🇨🇮' },
      { name: 'Cameroon', region: 'Central Africa', flag: '🇨🇲' },
      { name: 'Kenya', region: 'East Africa', flag: '🇰🇪' },
      { name: 'Uganda', region: 'East Africa', flag: '🇺🇬' },
      { name: 'Tanzania', region: 'East Africa', flag: '🇹🇿' },
      { name: 'Rwanda', region: 'East Africa', flag: '🇷🇼' },
      { name: 'Ethiopia', region: 'East Africa', flag: '🇪🇹' },
      { name: 'South Africa', region: 'Southern Africa', flag: '🇿🇦' },
      { name: 'Egypt', region: 'North Africa', flag: '🇪🇬' },
      { name: 'Morocco', region: 'North Africa', flag: '🇲🇦' },
    ],
  },
  'why-choose-us': {
    title: 'Why Choose Us',
    intro:
      'We do not just deliver services — we drive transformation. These nine commitments shape every engagement we accept.',
    points: [
      { name: 'Pan-African reach', body: 'Active across 13+ countries with on-the-ground delivery capability.' },
      { name: 'Global-standard delivery', body: 'Methodologies benchmarked against the best international practice.' },
      { name: 'Expert facilitators', body: 'Senior practitioners with deep institutional experience.' },
      { name: 'Certified programmes', body: 'Credentialled, verifiable and rigorously assessed.' },
      { name: 'Flexible delivery', body: 'Physical, virtual and blended modes available.' },
      { name: 'Dedicated support', body: 'Pre-engagement, during, and post-engagement accompaniment.' },
      { name: 'Measurable outcomes', body: 'Every engagement comes with defined deliverables and metrics.' },
      { name: 'Customised solutions', body: 'No generic programmes. Every engagement is shaped to the client.' },
      { name: 'Post-programme support', body: 'We stay engaged until the new systems are operating well.' },
    ],
  },
  'our-approach': {
    title: 'Our Approach',
    intro:
      'A disciplined, four-phase methodology that ensures every engagement we deliver produces operating outcomes — not paperwork.',
    phases: [
      {
        name: 'Diagnose',
        body:
          'We begin every engagement with rigorous listening and observation. Interviews with leadership and operating staff, document review, system walk-throughs, on-site visits — a complete picture of the institution before any recommendation is made.',
      },
      {
        name: 'Design',
        body:
          'Together with the client we design the operating systems, structures and policies the institution needs. Designs are practical, drawn on real African operating realities, and prepared to be operated by the team — not parked on a shelf.',
      },
      {
        name: 'Deploy',
        body:
          'Implementation. Documentation. Training. Change management. We work alongside the client to deploy the new systems and prepare the team to operate them confidently from day one.',
      },
      {
        name: 'Sustain',
        body:
          'The unglamorous but vital final phase. Six to twelve months of accompaniment, review and refinement until the new systems are embedded and the new capability is reliably running.',
      },
    ],
  },
  'contact-us': {
    title: 'Contact Us',
    intro:
      'We welcome confidential conversations about how we can support your institution. Reach out directly or send a structured enquiry through our contact form on the landing page.',
    channels: [
      { label: 'General enquiries', value: 'info@attpnexus.com.ng' },
      { label: 'Corporate Hub', value: 'OA@attpnexus.com.ng' },
      { label: 'Lead Consultant (direct)', value: 'oa@attpnexus.com.ng' },
      { label: 'Phone (Nigeria HQ)', value: '+234 (703) 313-7679' },
      { label: 'Phone (Lead Consultant)', value: '+234 (803) 517 5136' },
      { label: 'Operating hours', value: 'Monday – Friday · 8:00 – 18:00 (WAT)' },
    ],
    hqs: [
      { city: 'Lagos', state: 'Lagos State', country: 'Nigeria', flag: '🇳🇬' },
      { city: 'London', state: 'England', country: 'United Kingdom', flag: '🇬🇧' },
      { city: 'Houston', state: 'Texas', country: 'United States', flag: '🇺🇸' },
      { city: 'Toronto', state: 'Ontario', country: 'Canada', flag: '🇨🇦' },
    ],
  },
};
